/**
 * Django Data Source Adapter
 *
 * This adapter contains all the Django-specific logic extracted from the original
 * dataSourceAdapters.ts file. It preserves all the complex functionality while
 * providing a clean interface.
 */

import { IDataSourceAdapter, SearchOptions, SearchResult } from "./IDataSourceAdapter";
import { ApiEndpoint, StandardizedDataItem, FieldMapping, MediaItemDetailed, RelatedEntityItem } from "../../types";

// Import utilities that we'll need (these should stay in the main file)
import { apiFetch, buildItemDetailUrl, FetchOptions, REACT_APP_API_ROOT } from "../api";

// Import shared utilities
import {
  getNestedValue,
  extractField,
  getDjangoThumbnailUrl,
  COMMON_NAME_CANDIDATES,
  COMMON_DESCRIPTION_CANDIDATES
} from "./SharedAdapterUtils";

import { isCmTableEndpoint } from "../cmTableEndpoint";
import { normaliseCmTableRow, normaliseRelations } from "../../services/cmTables";

/**
 * Normalises `relationships` (if present) to the canonical `{ byTableSlug, media }`
 * shape for non-CM-table results (e.g. media), without the full CM table row
 * transformation applied by `normaliseCmTableRow`.
 */
function normaliseRowRelationships(row: any): any {
  if (!row || row.relationships === undefined) return row;
  return { ...row, relationships: normaliseRelations(row.relationships) };
}

export class DjangoAdapter implements IDataSourceAdapter {
  // ============================================================================
  // Core Data Operations
  // ============================================================================

  /**
   * Fetches data from a Django REST API endpoint with pagination and filtering support.
   *
   * Django-specific behavior:
   * - Uses Django REST Framework pagination format (results, next, previous, count)
   * - Supports field filtering via ?fields= query parameter for performance optimization
   * - Handles authentication through apiFetch utility with proper headers
   * - Supports Django's built-in search functionality via ?search= parameter
   *
   * Frontend UI Usage:
   * - DataGrid component: Loads paginated table data with sorting and filtering
   * - SearchBar component: Provides real-time search results as user types
   * - AdminComponent: Bulk data management and export functionality
   * - Used when user browses data tables, searches, or navigates pagination
   *
   * @param endpoint - Django API endpoint configuration with api_endpoint path
   * @param userProfile - User authentication profile for API access
   * @param options - Search options including filters, pagination, and field selection
   * @returns Promise resolving to paginated data with Django REST metadata
   */
  async fetchEndpointData(endpoint: ApiEndpoint, userProfile: any, options: SearchOptions = {}): Promise<SearchResult> {
    try {
      // Build URL with filters and pagination
      let url = options.paginationUrl || `${REACT_APP_API_ROOT}${endpoint.api_endpoint}`;

      const params = new URLSearchParams();

      // Add filters
      // CM table v2 endpoints use ?include_relations=true; v1 endpoints use ?include_rels=true.
      // Normalise here so callers don't need to know which param to use.
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          const normalisedKey = key === "include_rels" ? "include_relations" : key;
          params.append(normalisedKey, value);
        });
      }

      // Add fields selection
      if (options.fields) {
        params.append("fields", options.fields);
      }

      // Append query parameters
      if (params.toString()) {
        url += (url.includes("?") ? "&" : "?") + params.toString();
      }

      console.log(`[DjangoAdapter] Fetching data from: ${url}`);
      const response = await apiFetch(url);

      // Handle Django REST pagination format
      if (response.results && Array.isArray(response.results)) {
        const results = isCmTableEndpoint(endpoint)
          ? response.results.map(normaliseCmTableRow)
          : response.results.map(normaliseRowRelationships);
        return {
          data: results,
          pagination: {
            count: response.count || response.results.length,
            next: response.next || null,
            previous: response.previous || null
          }
        };
      }

      // Handle direct array response
      if (Array.isArray(response)) {
        const data = isCmTableEndpoint(endpoint) ? response.map(normaliseCmTableRow) : response.map(normaliseRowRelationships);
        return {
          data,
          pagination: null
        };
      }

      // Single item response
      return {
        data: [response],
        pagination: null
      };
    } catch (error) {
      console.error("[DjangoAdapter] Error fetching endpoint data:", error);
      throw error;
    }
  }

  /**
   * Searches a Django endpoint using Django's built-in search functionality.
   *
   * Django-specific behavior:
   * - Leverages Django REST Framework's search_fields configuration on the backend
   * - Uses ?search= query parameter which searches across configured model fields
   * - Supports full-text search capabilities if configured with PostgreSQL
   * - More efficient than client-side filtering as it's done at the database level
   *
   * Frontend UI Usage:
   * - SearchBar component: Real-time search as user types in search input
   * - DataGrid search functionality: Filter table results by search terms
   * - Quick search overlays: Find specific items across large datasets
   * - Used when user needs to find specific items by name, description, or content
   *
   * @param endpoint - Django API endpoint to search
   * @param searchTerm - Text to search for across configured search fields
   * @param userProfile - User authentication profile
   * @param options - Additional search options and filters
   * @returns Promise resolving to filtered search results from Django backend
   */
  async searchEndpoint(endpoint: ApiEndpoint, searchTerm: string, userProfile: any, options: SearchOptions = {}): Promise<SearchResult> {
    const searchOptions = {
      ...options,
      filters: {
        ...(options.filters || {}),
        search: searchTerm
      }
    };

    return this.fetchEndpointData(endpoint, userProfile, searchOptions);
  }

  /**
   * LEVEL 1: Fetches minimal item data optimized for fast loading (summary view).
   *
   * Django-specific optimizations:
   * - Uses ?fields= parameter to limit response to only essential fields
   * - Reduces payload size by 60-80% compared to full item fetch
   * - Perfect for dropdown lists, search results, and initial item previews
   * - Avoids expensive relationship queries and large text fields
   * - Returns standardized format with isComplete: false to indicate partial data
   *
   * Frontend UI Usage:
   * - OptionsContext: Populates dropdown lists with id/name pairs for MultiSelectDialog
   * - SearchBar results: Shows quick preview cards with name, description, thumbnail
   * - DataGrid initial load: Fast rendering of table rows before full data loads
   * - Used when speed is critical and full details aren't needed yet
   *
   * @param itemId - Django model primary key (usually integer)
   * @param endpoint - Django API endpoint configuration
   * @param userProfile - User authentication for API access
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to minimal item data (id, name, description, thumbnail)
   */
  async fetchItemSummary(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<any> {
    // LEVEL 1: Fetch minimal item data (name, description, thumbnail only)
    const url = buildItemDetailUrl(endpoint.api_endpoint, itemId, "?fields=id,name,description,thumbnail,image"); // Minimal fields for summary

    try {
      if (onProgress) {
        onProgress("Fetching item summary", 0, 2);
      }

      // Use apiFetch for proper authentication handling
      const rawItem = await apiFetch(url);

      if (onProgress) {
        onProgress("Processing summary", 1, 2);
      }

      // Return only essential fields for summary
      const summaryItem = {
        id: rawItem.id,
        name: rawItem.name || rawItem.title || `Django Item ${rawItem.id}`,
        description: rawItem.description || rawItem.summary || "",
        thumbnail: rawItem.thumbnail || rawItem.image || null,
        primary_image: rawItem.image ? { url: rawItem.image, thumbnail: rawItem.thumbnail } : null,
        created_at: rawItem.created_at || rawItem.date_created || null,
        last_updated_at: rawItem.updated_at || rawItem.date_modified || null,
        geometry: rawItem.geometry || null,
        coordinates: rawItem.coordinates || null,
        entityType: endpoint.tableName,
        isComplete: false, // Summary level is not complete
        _source: {
          type: "django",
          originalId: rawItem.id,
          rawData: rawItem
        }
      };

      if (onProgress) {
        onProgress("Summary complete", 2, 2);
      }

      console.log(`[DjangoAdapter] ✅ Fetched summary for item ${itemId}: "${summaryItem.name}"`);
      return summaryItem;
    } catch (error: any) {
      console.error(`[DjangoAdapter] Error fetching item summary ${itemId}:`, error);

      // Handle network errors gracefully
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("ERR_INTERNET_DISCONNECTED") ||
        error.message?.includes("Network Error")
      ) {
        console.warn(`[DjangoAdapter] Network error for Django item summary ${itemId}, creating fallback`);

        // Return a minimal summary entity
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "Network error prevented loading summary",
          thumbnail: null,
          primary_image: null,
          created_at: null,
          last_updated_at: null,
          geometry: null,
          coordinates: null,
          entityType: endpoint.tableName,
          isComplete: false,
          _source: {
            type: "django",
            originalId: itemId,
            rawData: {
              error: "Network error",
              fallbackReason: "Failed to fetch summary due to network issues"
            }
          }
        };
      }

      // Handle 404 errors
      if (error.message?.includes("404") || error.status === 404) {
        console.warn(`[DjangoAdapter] Django item summary ${itemId} not found (404)`);

        // Return a minimal summary indicating not found
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "This item could not be found",
          thumbnail: null,
          primary_image: null,
          created_at: null,
          last_updated_at: null,
          geometry: null,
          coordinates: null,
          entityType: endpoint.tableName,
          isComplete: false,
          _source: {
            type: "django",
            originalId: itemId,
            rawData: {
              error: "Item not found",
              fallbackReason: "404 error - item may have been deleted"
            }
          }
        };
      }

      throw error;
    }
  }

  /**
   * LEVEL 2: Fetches complete item data without relationships (default behavior).
   *
   * Django-specific behavior:
   * - Fetches all model fields but excludes expensive relationship queries
   * - Does NOT use ?include_rels=true to avoid N+1 queries and large payloads
   * - Perfect for displaying complete item details in DataItemDetails component
   * - Balances completeness with performance by deferring relationships
   * - Returns full item properties for rich field display and editing
   *
   * Frontend UI Usage:
   * - DataItemDetails: Shows complete item information when user clicks map or DataGrid
   * - SelectionContext: Default fetch level when user selects any entity
   * - ItemFormDialog: Loads full item data for editing (without heavy relationships)
   * - Used for main item display where all properties are needed but relationships can wait
   *
   * @param itemId - Django model primary key
   * @param endpoint - Django API endpoint configuration
   * @param userProfile - User authentication for API access
   * @param allApiEndpoints - All available endpoints for context (unused in Level 2)
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to complete item data without relationships
   */
  async fetchItem(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    allApiEndpoints: ApiEndpoint[] = [],
    onProgress?: (step: string, current: number, total: number) => void
  ): Promise<any> {
    // LEVEL 2: Fetch complete item data (all properties but no relationships)
    const url = buildItemDetailUrl(endpoint.api_endpoint, itemId); // No include_rels for level 2

    try {
      if (onProgress) {
        onProgress("Fetching complete item", 0, 2);
      }

      // Use apiFetch for proper authentication handling
      const rawItem = await apiFetch(url);

      if (onProgress) {
        onProgress("Processing item data", 1, 2);
      }
      const standardizedItem = this.adaptDjangoItem(rawItem, undefined, endpoint);

      if (onProgress) {
        onProgress("Item processing complete", 2, 2);
      }

      console.log(`[DjangoAdapter] ✅ Fetched complete item ${itemId}: "${standardizedItem.name}"`);
      return standardizedItem;
    } catch (error: any) {
      console.error(`[DjangoAdapter] Error fetching item ${itemId}:`, error);

      // Handle network errors gracefully
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("ERR_INTERNET_DISCONNECTED") ||
        error.message?.includes("Network Error")
      ) {
        console.warn(`[DjangoAdapter] Network error for Django item ${itemId}, creating fallback entity`);

        // Return a basic entity with the data we have from the grid
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "Network error prevented loading full details",
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          _source: {
            type: "django" as const,
            originalId: itemId,
            rawData: {
              error: "Network error",
              fallbackReason: "Failed to fetch due to network issues"
            }
          }
        };
      }

      // Handle 404 errors
      if (error.message?.includes("404") || error.status === 404) {
        console.warn(`[DjangoAdapter] Django item ${itemId} not found (404)`);

        // Return a basic entity that indicates the item wasn't found
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "This item could not be found. It may have been deleted.",
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          _source: {
            type: "django" as const,
            originalId: itemId,
            rawData: {
              error: "Item not found",
              fallbackReason: "404 error - item may have been deleted"
            }
          }
        };
      }

      throw error;
    }
  }

  /**
   * LEVEL 3: Fetches complete item data WITH all relationships (on-demand loading).
   *
   * Django-specific behavior:
   * - Uses ?include_rels=true to fetch related entities, media, and geo relationships
   * - Triggers complex database queries with joins and prefetch_related optimizations
   * - Returns comprehensive data structure with relationships.{related_entities, geo_entities, media}
   * - Most expensive operation but provides complete context for relationship visualization
   * - Used when user explicitly requests "Find Related" or when configured to always load relationships
   *
   * Frontend UI Usage:
   * - RelatedItemsRing: Displays connected entities when user clicks "Find Related"
   * - MediaDrawer: Shows all related media items and attachments
   * - SelectionContext: Used when alwaysRequestRelatedItemsAndMedia config is enabled
   * - DataItemDetails: Powers the full relationship visualization and navigation
   * - Used when user needs to see complete context and connected data
   *
   * @param itemId - Django model primary key
   * @param endpoint - Django API endpoint configuration
   * @param userProfile - User authentication for API access
   * @param allApiEndpoints - All available endpoints for relationship context
   * @param onProgress - Optional progress callback for UI feedback
   * @returns Promise resolving to complete item data with full relationship structure
   */
  async fetchItemWithRelationships(
    itemId: string | number,
    endpoint: ApiEndpoint,
    userProfile: any,
    allApiEndpoints: ApiEndpoint[] = [],
    onProgress?: (step: string, current: number, total: number) => void,
    options?: FetchOptions
  ): Promise<StandardizedDataItem> {
    // Media detail responses always include `relationships` — `?include_rels=true` is
    // unnecessary on the single-item endpoint and only needed on the media list endpoint.
    // CM table and spatial-dataset detail endpoints need `?include_relations=true`.
    const needsIncludeRelations = isCmTableEndpoint(endpoint) || endpoint.entityType === "spatial-dataset";
    const url = needsIncludeRelations
      ? buildItemDetailUrl(endpoint.api_endpoint, itemId, "?include_relations=true")
      : buildItemDetailUrl(endpoint.api_endpoint, itemId);

    try {
      console.log("[DjangoAdapter] Fetching item with relationships:", url);

      if (onProgress) {
        onProgress("Loading Related Datas...", 1, 2);
      }

      const completeData = await apiFetch(url, options);

      console.log("[DjangoAdapter] Successfully fetched Django item:", {
        itemId,
        hasRelationships: !!completeData.relationships,
        relationshipKeys: completeData.relationships ? Object.keys(completeData.relationships) : [],
        hasMedia: !!completeData.relationships?.media?.length
      });

      if (onProgress) {
        onProgress("Processing relationships", 2, 2);
      }

      // Transform the complete Django data into standardized format
      const standardizedItem = this.adaptDjangoItem(completeData, undefined, endpoint);

      // Ensure relationships structure is complete
      if (!standardizedItem.relationships) {
        standardizedItem.relationships = {
          related_entities: {},
          geo_entities: {},
          media: []
        };
      }

      // Process media relationships if they exist
      if (completeData.relationships?.media) {
        standardizedItem.relationships.media = this.processMediaRelationships(completeData.relationships.media);
      }

      // Process related entities if they exist
      if (completeData.relationships?.related_entities) {
        standardizedItem.relationships.related_entities = completeData.relationships.related_entities;
      }

      // Process geo entities if they exist
      if (completeData.relationships?.geo_entities) {
        standardizedItem.relationships.geo_entities = completeData.relationships.geo_entities;
      }

      return standardizedItem;
    } catch (error: any) {
      console.error("[DjangoAdapter] Error fetching Django item:", error);

      // Handle network errors gracefully
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("ERR_INTERNET_DISCONNECTED") ||
        error.message?.includes("Network Error")
      ) {
        console.warn(`[DjangoAdapter] Network error for Django item ${itemId} with relationships, creating fallback entity`);

        // Return a basic entity with the data we have
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "Network error prevented loading full details and relationships",
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          _source: {
            type: "django" as const,
            originalId: itemId,
            rawData: {
              error: "Network error",
              fallbackReason: "Failed to fetch relationships due to network issues"
            }
          }
        };
      }

      // If we get a 404, it might be because the item is in the wrong endpoint
      if (error instanceof Error && error.message.includes("404")) {
        console.warn("[DjangoAdapter] 404 error - item might be in wrong endpoint:", {
          itemId,
          triedEndpoint: endpoint.name,
          triedUrl: url
        });

        // Return a basic item structure to prevent crashes
        return {
          id: itemId,
          name: `Django Item ${itemId}`,
          description: "Complete data unavailable - item may be in different endpoint",
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          _source: {
            type: "django" as const,
            originalId: itemId,
            rawData: { error: "Item not found in expected endpoint" }
          }
        };
      }

      throw error;
    }
  }

  // ============================================================================
  // Display Methods - Using the sophisticated logic from original implementation
  // ============================================================================

  getDataItemName(item: any, endpoint?: ApiEndpoint): string {
    if (!item) return "Unknown Item";

    // Django items have standardized field names - keep it simple
    if (item.name && typeof item.name === "string" && item.name.trim()) {
      return item.name;
    }

    if (item.title && typeof item.title === "string" && item.title.trim()) {
      return item.title;
    }

    if (item.display_name && typeof item.display_name === "string" && item.display_name.trim()) {
      return item.display_name;
    }

    // Fallback to ID-based name
    return `${endpoint?.name || "Django"} Item ${item.id || "Unknown"}`;
  }

  getDataItemDescription(item: any, endpoint?: ApiEndpoint): string {
    if (!item) return "";

    // Django items have standardized field names - keep it simple
    if (item.description && typeof item.description === "string" && item.description.trim()) {
      return item.description;
    }

    if (item.summary && typeof item.summary === "string" && item.summary.trim()) {
      return item.summary;
    }

    if (item.notes && typeof item.notes === "string" && item.notes.trim()) {
      return item.notes;
    }

    return "";
  }

  getDataItemPrimaryImage(item: any): string | null {
    if (!item) return null;

    // Check for primary_image (standardized format)
    if (item.primary_image?.url) {
      return item.primary_image.url;
    }

    // Try common image fields
    const imageFields = ["image", "photo", "picture", "thumbnail"];

    for (const field of imageFields) {
      if (item[field] && typeof item[field] === "string") {
        return item[field];
      }
    }

    return null;
  }

  getItemThumbnail(item: any, endpoint?: any): string | null {
    if (!item) return null;

    // Use comprehensive Django thumbnail handling from shared utilities
    return getDjangoThumbnailUrl(item) || null;
  }

  // ============================================================================
  // Media & Relationships
  // ============================================================================

  /**
   * Extracts media items from a Django entity using Django's relationship structure.
   *
   * Django-specific media handling:
   * - Prioritizes relationships.media from ?include_rels=true API calls (most complete)
   * - Falls back to direct media arrays for processed items
   * - Handles Django's MediaItem model structure with file URLs and metadata
   * - Supports both direct media attachments and relationship-based media
   * - Returns standardized MediaItemDetailed format for consistent UI rendering
   *
   * Frontend UI Usage:
   * - MediaDrawer: Displays photo/video thumbnails and enables media viewing
   * - DataItemDetails: Shows primary image and media count indicators
   * - MediaViewer: Opens full-size media when user clicks thumbnails
   * - Used whenever user needs to see photos, videos, or documents attached to an item
   *
   * @param item - Django entity that may contain media relationships
   * @returns Array of standardized media items ready for MediaDrawer display
   */
  getRelatedMedia(item: any): MediaItemDetailed[] {
    if (!item) return [];

    console.log("[DjangoAdapter] getRelatedMedia called with item:", {
      itemId: item.id,
      itemName: item.name,
      hasRelationships: !!item.relationships,
      hasRelationshipsMedia: !!item.relationships?.media,
      relationshipsMediaLength: item.relationships?.media?.length || 0,
      hasDirectMedia: !!item.media,
      hasRelatedMedia: !!item.relatedMedia,
      hasLegacyRelatedMedia: !!item.related_media
    });

    // 1. First priority: relationships.media (from ?include_rels=true API calls)
    if (item.relationships?.media && Array.isArray(item.relationships.media)) {
      console.log(`[DjangoAdapter] Found relationships.media for Django item:`, {
        itemId: item.id,
        itemName: item.name,
        mediaCount: item.relationships.media.length,
        mediaTypes: item.relationships.media.map((m: any) => m.media_type || "unknown").slice(0, 3)
      });

      return item.relationships.media.map((media: any, index: number) => {
        // Normalize remote_details shape (can arrive as JSON string depending on serializer)
        let remoteDetails = media.remote_details;
        if (typeof remoteDetails === "string") {
          try {
            remoteDetails = JSON.parse(remoteDetails);
          } catch {
            // keep original string if not JSON
          }
        }

        const embedUrl = media.embed_url || remoteDetails?.embed_url;
        const thumbnail = media.thumbnail || media.thumbnail_url || remoteDetails?.thumbnail || null;

        return {
          id: media.id || `media-${index}`,
          name: media.name || media.title || `Media ${index + 1}`,
          description: media.entity_description || media.description || "",
          type: media.media_type || media.type || "unknown",
          media_type: media.media_type || media.type || "unknown",
          url: media.url || media.file_url || "",
          thumbnail,
          embed_url: embedUrl,
          remote_details: remoteDetails,
          original_file: {
            filename: media.filename || media.name || `media_${media.id || index}`,
            type: media.content_type || media.media_type || media.type || "unknown",
            size: media.file_size ? media.file_size.toString() : "unknown"
          },
          relationships: {
            related_entities: {},
            geo_entities: {},
            media: []
          },
          file_size: media.file_size || null,
          created_at: media.created_at || null,
          metadata: {
            relationship_id: media.relationship_id,
            rel_description: media.rel_description,
            ...media.metadata
          }
        };
      });
    }

    // 2. Second priority: Direct relatedMedia array (processed Django items)
    if (item.relatedMedia && Array.isArray(item.relatedMedia)) {
      console.log(`[DjangoAdapter] Found relatedMedia for Django item:`, {
        itemId: item.id,
        itemName: item.name,
        relatedMediaCount: item.relatedMedia.length,
        mediaTypes: item.relatedMedia.map((m: any) => m.media_type || "unknown").slice(0, 3)
      });
      return item.relatedMedia;
    }

    // 3. Third priority: Legacy related_media field
    if (item.related_media && Array.isArray(item.related_media)) {
      console.log(`[DjangoAdapter] Found legacy related_media for Django item:`, {
        itemId: item.id,
        itemName: item.name,
        relatedMediaCount: item.related_media.length
      });
      return item.related_media;
    }

    // 4. Fourth priority: Direct media array
    if (item.media && Array.isArray(item.media)) {
      return item.media.map((media: any, index: number) => ({
        id: media.id || `media-${index}`,
        name: media.name || media.title || `Media ${index + 1}`,
        description: media.description || media?.entity_description || "",
        type: media.media_type || media.type || "unknown",
        media_type: media.media_type || media.type || "unknown",
        url: media.url || media.file_url || "",
        thumbnail: media.thumbnail || media.thumbnail_url || null,
        embed_url: media.embed_url,
        remote_details: media.remote_details,
        original_file: {
          filename: media.filename || media.name || `media_${media.id || index}`,
          type: media.content_type || media.type || "unknown",
          size: media.file_size ? media.file_size.toString() : "unknown"
        },
        relationships: {
          related_entities: {},
          geo_entities: {},
          media: []
        },
        file_size: media.file_size || null,
        created_at: media.created_at || null,
        metadata: media.metadata || {}
      }));
    }

    // 5. Final fallback: If it's a media item itself, return it as the only related media
    if (item.url || item.thumbnail || item.media_type) {
      console.log(`[DjangoAdapter] Using Django item itself as media:`, {
        itemId: item.id,
        itemName: item.name,
        mediaType: item.media_type || "unknown",
        hasUrl: !!item.url,
        hasThumbnail: !!item.thumbnail
      });
      return [item];
    }

    console.log(`[DjangoAdapter] No media found for Django item:`, {
      itemId: item.id,
      itemName: item.name
    });
    return [];
  }

  // ============================================================================
  // Map Operations
  // ============================================================================

  /**
   * Enhances map click data for Django GeoJSON features to work with 3-level adapter system.
   *
   * Django-specific map handling:
   * - Extracts Django model primary key from feature properties
   * - Django features typically have complete data already loaded in GeoJSON
   * - Sets hasCompleteData: true since Django map layers often include full data
   * - Preserves spatial data and all feature properties
   * - Maps to endpoint.tableName for consistent entity typing
   * - Compatible with Django's spatial database capabilities (PostGIS)
   *
   * @param feature - GeoJSON feature from Django map layer
   * @param layerId - Map layer identifier
   * @param endpoint - Django endpoint configuration
   * @param config - Optional configuration
   * @returns Promise resolving to enhanced entity for SelectionContext processing
   */
  async enhanceMapClickData(feature: any, layerId: string, endpoint?: ApiEndpoint, config?: any): Promise<any> {
    // Django-specific map click enhancement
    return {
      id: feature.properties?.id || feature.id || "unknown",
      name: feature.properties?.name || feature.name || `${layerId} Feature`,
      entityType: endpoint?.tableName || layerId,
      layerId: layerId,
      coordinates: feature.geometry?.type === "Point" ? (feature.geometry.coordinates as [number, number]) : undefined,
      geometry: feature.geometry,
      properties: {
        ...feature.properties,
        fromMapClick: true,
        hasCompleteData: true, // Django typically has complete data
        dataSourceType: "django"
      }
    };
  }

  extractFeatureData(properties: any, layerId: string, endpoint?: ApiEndpoint): any {
    // Django feature data extraction
    return {
      ...properties,
      layerId,
      dataSourceType: "django",
      tableName: endpoint?.tableName || layerId
    };
  }

  // ============================================================================
  // Data Transformation
  // ============================================================================

  async adaptDataSource(
    rawData: any,
    fieldMapping?: FieldMapping,
    apiEndpoints?: ApiEndpoint[],
    endpoint?: ApiEndpoint
  ): Promise<StandardizedDataItem[]> {
    // Handle Django REST pagination
    if (rawData.results && Array.isArray(rawData.results)) {
      return rawData.results.map((item: any) => this.adaptDjangoItem(item, fieldMapping, endpoint));
    }

    if (Array.isArray(rawData)) {
      return rawData.map((item: any) => this.adaptDjangoItem(item, fieldMapping, endpoint));
    }

    return [this.adaptDjangoItem(rawData, fieldMapping, endpoint)];
  }

  // ============================================================================
  // Media Entity Methods
  // ============================================================================

  canMediaItemBeStandaloneEntity(mediaItem: any): boolean {
    // Django media items are standalone database records with their own IDs
    return true;
  }

  canFetchMediaItemIndependently(mediaItem: any): boolean {
    // Django media items can be fetched independently via /api/v2/media/{id}/
    return true;
  }

  canMediaItemHaveRelationships(mediaItem: any): boolean {
    // Django media items can have relationships with other entities
    return true;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  getDataSourceType(item?: any, endpoint?: any): string {
    return "django";
  }

  isEndpointEditable(endpoint: { dataSourceType?: string; isEditable?: boolean }): boolean {
    // Django endpoints are typically editable unless explicitly marked otherwise
    return endpoint.isEditable !== false;
  }

  /**
   * Django items use internal dialog for editing (current behavior)
   *
   * Django-specific behavior:
   * - Returns false to indicate internal dialog should be used
   * - Preserves existing ItemFormDialog functionality
   * - Maintains current user experience for Django items
   *
   * @param item - The Django item to edit
   * @param endpoint - Endpoint configuration (can be null)
   * @returns false (use internal dialog)
   */
  openItemForEditing(item: any, endpoint?: ApiEndpoint | null): boolean {
    // Django items use the internal ItemFormDialog - return false to use existing behavior
    return false;
  }

  isImageUrl(url: string | null): boolean {
    if (!url || typeof url !== "string") return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff)(\?|$)/i;
    if (imageExtensions.test(url)) return true;

    // Check for data URLs
    if (url.startsWith("data:image/")) return true;

    // Check for blob URLs (often used for uploaded images)
    if (url.startsWith("blob:")) return true;

    return false;
  }

  async findCorrectEndpointForItem(
    itemId: string | number,
    itemName: string,
    apiEndpoints: ApiEndpoint[],
    currentEndpoint: ApiEndpoint
  ): Promise<ApiEndpoint> {
    // For Django, usually the current endpoint is correct
    // This is a simplified implementation - the original has more complex logic
    return currentEndpoint;
  }

  getThumbnailHandler(dataSourceType: string): (item: any) => string | undefined {
    return (item: any) => {
      return this.getItemThumbnail(item) || undefined;
    };
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  private getApiRoot(): string {
    // Use the imported REACT_APP_API_ROOT constant
    return REACT_APP_API_ROOT;
  }

  private processMediaRelationships(mediaArray: any[]): RelatedEntityItem[] {
    if (!Array.isArray(mediaArray)) return [];

    return mediaArray.map((mediaItem: any) => ({
      id: mediaItem.id || "unknown",
      name: mediaItem.name || mediaItem.title || "Unnamed Media",
      description: mediaItem.description || mediaItem?.entity_description || "",
      type: mediaItem.media_type || mediaItem.type || "media",
      thumbnail: mediaItem.thumbnail || mediaItem.thumbnail_url || null,
      url: mediaItem.url || mediaItem.file_url || null,
      media_type: mediaItem.media_type || mediaItem.type || "unknown",
      is_primary: mediaItem.is_primary,
      relationship_id: mediaItem.relationship_id,
      relationships: {
        related_entities: {},
        geo_entities: {},
        media: []
      }
    }));
  }

  private adaptDjangoItem(rawItem: any, fieldMapping?: FieldMapping, endpoint?: ApiEndpoint): StandardizedDataItem {
    // For CM Table endpoints, pre-process the raw V2 wire row into canonical shape
    // before any field extraction. This is the single boundary for all V2 differences.
    const item = isCmTableEndpoint(endpoint) ? normaliseCmTableRow(rawItem) : rawItem;

    return {
      id: extractField(item, fieldMapping?.id || "id"),
      name: extractField(item, fieldMapping?.name || "name"),
      description: extractField(item, fieldMapping?.description || "description"),
      thumbnail: extractField(item, fieldMapping?.thumbnail || "thumbnail"),
      created_at: extractField(item, fieldMapping?.created_at || "created_at"),
      last_updated_at: extractField(item, fieldMapping?.last_updated_at || "last_updated_at"),
      url: extractField(item, fieldMapping?.url || "url"),
      media_type: extractField(item, fieldMapping?.media_type || "media_type"),
      embed_url: item.embed_url,
      remote_details: item.remote_details,
      geometry: extractField(item, fieldMapping?.geometry || "geometry"),
      coordinates: extractField(item, fieldMapping?.coordinates || "coordinates"),

      // Always normalise relationships to { byTableSlug, media } shape.
      // normaliseRelations is idempotent so CM table rows (already normalised) pass through unchanged.
      relationships: item.relationships !== undefined ? normaliseRelations(item.relationships) : undefined,

      _source: {
        type: "django" as const,
        originalId: item.id || "unknown",
        rawData: item
      },

      ...(item?.primary_image && { primary_image: item?.primary_image }),
      ...(Array.isArray(item?.restrictions) && { restrictions: item.restrictions }),

      // Preserve CM-table schema-driven custom fields so EntityForm can re-seed
      // its `rowData` (form capture inputs) when reopening a saved row for edit.
      ...(item?.row_data && typeof item.row_data === "object" && { row_data: item.row_data })
    };
  }
}
